import { useEffect, useState } from "react";
import { Link, useParams } from "react-router";
import { useQuery, useMutation } from "@apollo/client";
import {
  Play,
  X,
  Check,
  MapPin,
  Mail,
  Phone,
  Video,
  FileText,
  CreditCard,
  ArrowRightLeft,
  Clock,
  Send,
} from "lucide-react";
import { Avatar, Card, ClientName, clientInitials, clientDisplayName, DocumentViewer, FeeAgreementViewer, Tooltip } from "../components/ui";
import { useToastPush } from "../components/ui/Toast";
import {
  GET_SESSION_DETAIL,
  GET_BOOKING_FILES,
  GET_BOOKING_PAYMENTS,
  GET_BOOKING_MOVES,
  GET_BOOKING_CLOSURES,
  GET_PRIMARY_CUSTOMER,
  GET_PROVIDERS,
  GET_SESSION_TRANSCRIPT,
  RESTORE_BOOKING,
} from "../apollo/sessions";
import { SEND_SESSION_LINK } from "../apollo/conductSession";
import {
  CANCELLABLE_STATES,
  MOVABLE_STATES,
  NON_CLOSABLE_STATES,
} from "../utils/sessionStatus";
import { BookingProgressBar, BookingStateBadge, PageHeader } from "../components/ui";
import {
  formatRelative,
  formatTimeRange,
  formatDuration,
  formatDateLabel,
  methodToLocation,
  formatFileSize,
  timeAgo,
  parseDateTime,
} from "../utils/sessionTime";

// ── Tab type ─────────────────────────────────────────────────────────

type Tab = "overview" | "files" | "activity" | "transcript";

const STATES_WITHOUT_FEE_AGREEMENT = new Set([
  "pending_auth",
  "auth_completed",
  "fee_ag_completed",
  "processing",
]);

// ── Helpers ──────────────────────────────────────────────────────────

interface BookingFile {
  id: string;
  originalFilename: string;
  fileSize: number;
  mimeType: string;
  createdAt: string;
}

function humanize(s: string): string {
  return s.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-[11px] text-ink-4 uppercase tracking-wider font-medium mb-1">
        {label}
      </dt>
      <dd className="text-[13px] font-medium text-ink-2">{value}</dd>
    </div>
  );
}

function fileIconClass(mimeType: string): string {
  if (mimeType.includes("pdf")) return "bg-rust-s text-rust";
  if (mimeType.startsWith("image/")) return "bg-tide-ss text-tide";
  return "bg-sage-s text-sage";
}

function FileListItem({
  file,
  selected,
  onSelect,
}: {
  file: BookingFile;
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={`w-full text-left px-4 py-3 flex items-center gap-3 transition border-l-2 ${
        selected
          ? "bg-tide-ss/60 border-tide"
          : "border-transparent hover:bg-paper-2"
      }`}
    >
      <div
        className={`w-8 h-9 rounded flex items-center justify-center flex-shrink-0 ${fileIconClass(file.mimeType)}`}
      >
        <FileText size={13} strokeWidth={2} />
      </div>
      <div className="flex-1 min-w-0">
        <div
          className={`text-[12.5px] truncate ${selected ? "font-semibold text-ink" : "font-medium text-ink-2"}`}
        >
          {file.originalFilename}
        </div>
        <div className="text-[11px] text-ink-4 truncate">
          {formatFileSize(file.fileSize)} · {timeAgo(file.createdAt)}
        </div>
      </div>
    </button>
  );
}

function FilesPanel({
  files,
  selectedFileId,
  onSelect,
}: {
  files: BookingFile[];
  selectedFileId: string | null;
  onSelect: (id: string) => void;
}) {
  if (files.length === 0) {
    return (
      <Card>
        <div className="py-12 text-center text-[13px] text-ink-4">
          No files attached to this session.
        </div>
      </Card>
    );
  }

  const selected = files.find((f) => f.id === selectedFileId) ?? files[0];

  return (
    <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-start">
      {/* Left: file list */}
      <div className="md:col-span-4 lg:col-span-3">
        <Card
          header={{
            title: `${files.length} document${files.length !== 1 ? "s" : ""}`,
          }}
        >
          <div className="divide-hair max-h-[640px] overflow-y-auto">
            {files.map((file) => (
              <FileListItem
                key={file.id}
                file={file}
                selected={selected.id === file.id}
                onSelect={() => onSelect(file.id)}
              />
            ))}
          </div>
        </Card>
      </div>

      {/* Right: viewer */}
      <div className="md:col-span-8 lg:col-span-9">
        <div className="mb-2 flex items-center justify-between gap-2 px-1">
          <div className="min-w-0">
            <div className="text-[14px] font-semibold text-ink truncate">
              {selected.originalFilename}
            </div>
            <div className="text-[11.5px] text-ink-4">
              {formatFileSize(selected.fileSize)} · {selected.mimeType} ·
              uploaded {timeAgo(selected.createdAt)}
            </div>
          </div>
        </div>
        <DocumentViewer fileId={selected.id} key={selected.id} />
      </div>
    </div>
  );
}

// ── Transcript panel ────────────────────────────────────────────────

interface TranscriptTurn {
  participantId: string | null;
  participantRole: string | null;
  participantName: string | null;
  offsetSeconds: number | null;
  startMs: number | null;
  endMs: number | null;
  text: string | null;
}

type TranscriptStage =
  | "live"
  | "awaiting_recordings"
  | "archiving"
  | "transcribing"
  | "awaiting_grace"
  | "merging"
  | "ready"
  | "partial"
  | "failed"
  | null;

interface SessionTranscript {
  sessionId: string;
  ready: boolean;
  generatedAt: string | null;
  error: string | null;
  turns: TranscriptTurn[] | null;
  stage: TranscriptStage;
  totalTracks: number | null;
  archivedTracks: number | null;
  transcribedTracks: number | null;
  mergedTracks: number | null;
  failedTracks: number | null;
  estimatedReadyAt: string | null;
}

const TERMINAL_STAGES = new Set<TranscriptStage>([
  "ready",
  "partial",
  "failed",
]);

function formatOffset(seconds: number | null): string {
  if (seconds == null) return "--:--";
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

function speakerLabel(t: TranscriptTurn): string {
  if (t.participantName) return t.participantName;
  // Accept both 'provider' and 'lawyer' during one deploy cycle; drop 'lawyer' next cycle
  if (t.participantRole === "provider" || t.participantRole === "lawyer") return "Provider";
  if (t.participantRole === "customer") return "Client";
  return "Unknown";
}

function speakerToneClass(role: string | null): string {
  // Tide for the provider, deep ink for clients. Picks up the same palette
  // the rest of the portal uses for role-coloured chips.
  // Accept both 'provider' and 'lawyer' during one deploy cycle; drop 'lawyer' next cycle
  if (role === "provider" || role === "lawyer") return "text-tide";
  if (role === "customer") return "text-deep";
  return "text-ink-4";
}

// ── Progress card ────────────────────────────────────────────────────
// Maps the resolver's `stage` field onto a 4-step pipeline visual so the
// user sees that something IS happening between hangup and the merged
// transcript arriving.

interface StepDef {
  key: "archive" | "transcribe" | "assemble" | "ready";
  label: string;
}

const STEPS: StepDef[] = [
  { key: "archive", label: "Save audio" },
  { key: "transcribe", label: "Transcribe" },
  { key: "assemble", label: "Assemble" },
  { key: "ready", label: "Ready" },
];

function stepStatus(
  step: StepDef["key"],
  stage: TranscriptStage,
): "complete" | "active" | "pending" {
  // Map each pipeline stage to which step is currently active. Anything
  // before the active step is complete; anything after is pending.
  const order: Record<Exclude<TranscriptStage, null>, StepDef["key"]> = {
    live: "archive",
    awaiting_recordings: "archive",
    archiving: "archive",
    transcribing: "transcribe",
    awaiting_grace: "assemble",
    merging: "assemble",
    ready: "ready",
    partial: "ready",
    failed: "ready",
  };
  const stepIdx = STEPS.findIndex((s) => s.key === step);
  const activeKey = stage ? order[stage] : "archive";
  const activeIdx = STEPS.findIndex((s) => s.key === activeKey);
  if (stage === "ready" || stage === "partial") return "complete";
  if (stage === "failed" && step === "ready") return "active";
  if (stepIdx < activeIdx) return "complete";
  if (stepIdx === activeIdx) return "active";
  return "pending";
}

function progressSubline(t: SessionTranscript): string {
  const total = t.totalTracks ?? 0;
  switch (t.stage) {
    case "live":
      return "Session is in progress — the transcript will appear after the call ends.";
    case "awaiting_recordings":
      return "Wrapping up — waiting for Twilio to deliver the recording files.";
    case "archiving":
      return total > 0
        ? `Saving audio to secure storage: ${t.archivedTracks ?? 0} of ${total} tracks.`
        : "Saving audio to secure storage…";
    case "transcribing":
      return total > 0
        ? `Transcribing: ${t.transcribedTracks ?? 0} of ${total} tracks.`
        : "Transcribing…";
    case "awaiting_grace": {
      if (!t.estimatedReadyAt) return "Almost ready — waiting for stragglers.";
      // Naive-UTC ISO strings (no offset) parse as LOCAL time — anchor them.
      const iso = /Z$|[+-]\d{2}:\d{2}$/.test(t.estimatedReadyAt)
        ? t.estimatedReadyAt
        : `${t.estimatedReadyAt}Z`;
      const ms = new Date(iso).getTime() - Date.now();
      if (ms <= 0) return "Almost ready — assembling now.";
      const minutes = Math.max(1, Math.ceil(ms / 60_000));
      return `Almost ready — assembling in ~${minutes} min.`;
    }
    case "merging":
      return "Assembling the transcript.";
    case "failed":
      return "We couldn't generate a transcript for this session. Contact support if this looks wrong.";
    default:
      return "Processing transcript…";
  }
}

function TranscriptProgressCard({ transcript }: { transcript: SessionTranscript }) {
  const failed = transcript.stage === "failed";
  return (
    <Card header={{ title: "Session transcript" }}>
      <div className="p-6 space-y-5">
        <div className="text-[13.5px] text-ink-2">
          {progressSubline(transcript)}
        </div>

        {/* Stepper — equal-width grid cells; each cell draws the connector
            back to the previous dot as an absolutely positioned line at
            dot height, so lines always run dot-center to dot-center and
            labels always sit centered under their dot. */}
        <div className="grid grid-cols-4">
          {STEPS.map((step, idx) => {
            const status = stepStatus(step.key, transcript.stage);
            const reached = status === "complete" || status === "active";
            const dotColor = failed
              ? "bg-rust"
              : status === "complete"
                ? "bg-sage"
                : status === "active"
                  ? "bg-tide animate-pulse"
                  : "bg-ink-5";
            const labelColor =
              status === "active"
                ? "text-ink"
                : status === "complete"
                  ? "text-ink-2"
                  : "text-ink-4";
            return (
              <div
                key={step.key}
                className="relative flex flex-col items-center gap-1.5"
              >
                {idx > 0 && (
                  <div
                    className={`absolute top-[4px] right-1/2 w-full h-0.5 rounded-full ${
                      failed && reached
                        ? "bg-rust/40"
                        : reached
                          ? "bg-sage"
                          : "bg-ink-5/50"
                    }`}
                  />
                )}
                <span
                  className={`relative z-10 w-2.5 h-2.5 rounded-full ring-4 ring-paper-hi ${dotColor}`}
                />
                <span className={`text-[10.5px] font-medium ${labelColor}`}>
                  {step.label}
                </span>
              </div>
            );
          })}
        </div>

        {transcript.totalTracks != null && transcript.totalTracks > 0 && (
          <div className="text-[11.5px] text-ink-4 font-mono">
            {transcript.archivedTracks ?? 0} archived ·{" "}
            {transcript.transcribedTracks ?? 0} transcribed ·{" "}
            {transcript.mergedTracks ?? 0} merged
            {(transcript.failedTracks ?? 0) > 0 &&
              ` · ${transcript.failedTracks} failed`}
            {transcript.totalTracks &&
              ` (of ${transcript.totalTracks} total)`}
          </div>
        )}
      </div>
    </Card>
  );
}

function TranscriptPanel({
  loading,
  transcript,
}: {
  loading: boolean;
  transcript: SessionTranscript | undefined;
}) {
  if (loading && !transcript) {
    return (
      <Card>
        <div className="py-12 text-center text-[13px] text-ink-4">
          Loading transcript…
        </div>
      </Card>
    );
  }

  if (!transcript) {
    return (
      <Card>
        <div className="py-12 text-center text-[13px] text-ink-4">
          Transcript will appear here once the session is processed.
        </div>
      </Card>
    );
  }

  // Spaces load failure on an already-merged session — surface the error
  // inside the otherwise-ready tab rather than the progress card.
  if (transcript.error) {
    return (
      <Card>
        <div className="py-12 text-center text-[13px] text-rust">
          {transcript.error}
        </div>
      </Card>
    );
  }

  // Not ready (or no turns) → progress card. Covers live / archiving /
  // transcribing / awaiting_grace / merging / failed.
  if (!transcript.ready || !transcript.turns) {
    return <TranscriptProgressCard transcript={transcript} />;
  }

  const partial = transcript.stage === "partial";

  return (
    <Card
      header={{
        title: "Session transcript",
        meta: transcript.generatedAt
          ? `Generated ${timeAgo(transcript.generatedAt)}`
          : undefined,
      }}
    >
      {partial && (transcript.failedTracks ?? 0) > 0 && (
        <div className="px-5 py-3 bg-rust-s/40 text-[12px] text-ink-2 hair-b">
          {transcript.failedTracks} segment
          {transcript.failedTracks === 1 ? "" : "s"} couldn't be transcribed —
          the transcript below is missing that audio.
        </div>
      )}
      <div className="divide-hair max-h-[640px] overflow-y-auto">
        {transcript.turns.map((t, idx) => (
          <div
            key={`${t.startMs ?? idx}-${t.participantId ?? "unknown"}`}
            className="px-5 py-3 grid grid-cols-[4rem_8rem_1fr] gap-3 items-baseline"
          >
            <span className="text-[11px] font-mono text-ink-4 num pt-0.5">
              {formatOffset(t.offsetSeconds)}
            </span>
            <span
              className={`text-[12.5px] font-semibold truncate ${speakerToneClass(t.participantRole)}`}
            >
              {speakerLabel(t)}
            </span>
            <span className="text-[13.5px] text-ink leading-relaxed">
              {t.text}
            </span>
          </div>
        ))}
      </div>
    </Card>
  );
}

// ── Component ────────────────────────────────────────────────────────

export function SessionDetail() {
  const { id } = useParams();
  const [activeTab, setActiveTab] = useState<Tab>("overview");
  const [selectedFileId, setSelectedFileId] = useState<string | null>(null);
  const [agreementOpen, setAgreementOpen] = useState(false);

  const toast = useToastPush();

  // Core booking data — includeDeleted so we can view deleted bookings
  const { data, loading, refetch: refetchDetail } = useQuery(GET_SESSION_DETAIL, {
    variables: { id, includeDeleted: true },
    skip: !id,
  });
  const booking = data?.Booking;

  const isDeleted = Boolean(booking?.deletedAt);

  const [restoreBooking, { loading: restoring }] = useMutation(RESTORE_BOOKING);

  const handleRestore = async () => {
    try {
      await restoreBooking({ variables: { bookingId: id } });
      toast({ kind: "success", title: "Session restored" });
      refetchDetail();
    } catch {
      toast({ kind: "error", title: "Failed to restore session" });
    }
  };

  const [sendSessionLink, { loading: sendingLink }] = useMutation(SEND_SESSION_LINK);
  const [linkJustSent, setLinkJustSent] = useState(false);

  const handleSendLink = async () => {
    try {
      const { data } = await sendSessionLink({ variables: { bookingId: id } });
      if (data?.sendSessionLink?.success) {
        toast({ kind: "success", title: "Join link sent to client" });
        setLinkJustSent(true);
        setTimeout(() => setLinkJustSent(false), 5000);
      } else {
        toast({
          kind: "error",
          title: "Couldn't send join link",
          description: data?.sendSessionLink?.error ?? undefined,
        });
      }
    } catch {
      toast({ kind: "error", title: "Couldn't send join link" });
    }
  };

  // Supplemental queries
  const { data: filesData } = useQuery(GET_BOOKING_FILES, {
    variables: { filter: { bookingId: id }, sortField: "created_at", sortOrder: "DESC" },
    skip: !id,
  });

  const { data: paymentsData } = useQuery(GET_BOOKING_PAYMENTS, {
    variables: { filter: { bookingId: id }, sortField: "created_at", sortOrder: "DESC" },
    skip: !id,
  });

  const { data: movesData } = useQuery(GET_BOOKING_MOVES, {
    variables: { filter: { bookingId: id }, sortField: "created_at", sortOrder: "DESC" },
    skip: !id,
  });

  const { data: closuresData } = useQuery(GET_BOOKING_CLOSURES, {
    variables: { filter: { bookingId: id }, sortField: "created_at", sortOrder: "DESC" },
    skip: !id,
  });

  const { data: customerData } = useQuery(GET_PRIMARY_CUSTOMER, {
    variables: { filter: { bookingId: id, isPrimary: true } },
    skip: !id,
  });

  const { data: providerData } = useQuery(GET_PROVIDERS, {
    variables: { perPage: 1000, sortField: "lastName", sortOrder: "ASC" },
  });

  // Transcript is fetched only when the user opens the tab — keeps the page
  // load lean for sessions that don't have one yet.
  const {
    data: transcriptData,
    loading: transcriptLoading,
    startPolling: startTranscriptPolling,
    stopPolling: stopTranscriptPolling,
  } = useQuery(GET_SESSION_TRANSCRIPT, {
    variables: { bookingId: id },
    skip: !id || activeTab !== "transcript",
  });

  // Poll every 10s while the pipeline is still working, stop once it
  // settles (ready / partial / failed). Stays off when the tab isn't
  // open — Apollo's skip pauses both the initial fetch and polling.
  const currentStage: TranscriptStage =
    transcriptData?.sessionTranscript?.stage ?? null;
  useEffect(() => {
    if (activeTab !== "transcript") {
      stopTranscriptPolling();
      return;
    }
    if (currentStage && TERMINAL_STAGES.has(currentStage)) {
      stopTranscriptPolling();
    } else {
      startTranscriptPolling(10000);
    }
    return () => stopTranscriptPolling();
  }, [activeTab, currentStage, startTranscriptPolling, stopTranscriptPolling]);

  // Derived data
  const files: BookingFile[] = filesData?.allBookingFiles ?? [];
  const payments = paymentsData?.allPayments ?? [];
  const moves = movesData?.allBookingMoves ?? [];
  const closures = closuresData?.allBookingClosures ?? [];
  const primaryCustomer = customerData?.allPeople?.[0] ?? null;
  const allProviders = providerData?.allProviders ?? [];
  interface ProviderRow { id: string; firstName: string; lastName: string }
  const providerMap = new Map<string, ProviderRow>(
    allProviders.map((l: ProviderRow) => [l.id, l]),
  );

  const avail = booking?.availability;

  const provider: ProviderRow | undefined = avail ? providerMap.get(avail.providerId) : undefined;
  const providerName = provider ? `${provider.firstName} ${provider.lastName}` : null;
  const providerInitials = provider
    ? `${provider.firstName[0]}${provider.lastName[0]}`
    : "??";

  const canCancel = booking ? CANCELLABLE_STATES.has(booking.state) : false;
  const canMove = booking ? MOVABLE_STATES.has(booking.state) : false;
  const canClose = booking ? !NON_CLOSABLE_STATES.has(booking.state) : false;

  // Auto-select the first file when the list arrives or changes underneath us
  useEffect(() => {
    if (files.length === 0) {
      if (selectedFileId !== null) setSelectedFileId(null);
      return;
    }
    if (!selectedFileId || !files.some((f) => f.id === selectedFileId)) {
      setSelectedFileId(files[0].id);
    }
  }, [files, selectedFileId]);

  useEffect(() => {
    if (!agreementOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setAgreementOpen(false);
    };
    window.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [agreementOpen]);

  const feeAgreementAvailable =
    booking != null && !STATES_WITHOUT_FEE_AGREEMENT.has(booking.state);

  // Loading state
  if (loading || !booking) {
    return (
      <>
        <PageHeader heading={false} back={{ to: "/sessions", label: "sessions" }} />
        <div className="px-4 md:px-7 pb-20">
        <div className="animate-pulse space-y-6">
          <div className="h-10 w-96 rounded bg-canvas" />
          <div className="h-5 w-64 rounded bg-canvas" />
          <div className="grid grid-cols-12 gap-6">
            <div className="col-span-8 space-y-3">
              <div className="h-48 rounded-lg bg-canvas" />
              <div className="h-32 rounded-lg bg-canvas" />
            </div>
            <div className="col-span-4 space-y-3">
              <div className="h-40 rounded-lg bg-canvas" />
              <div className="h-32 rounded-lg bg-canvas" />
            </div>
          </div>
        </div>
        </div>
      </>
    );
  }

  // Questions rendering helper
  const questions = booking.questions;
  const questionEntries: { label: string; value: string }[] = [];
  if (questions) {
    if (questions.isTimeSensitive)
      questionEntries.push({ label: "Time sensitive", value: questions.isTimeSensitive });
    if (questions.isInvolvingTheCourt)
      questionEntries.push({ label: "Involving the court", value: questions.isInvolvingTheCourt });
    if (questions.isPaperworkAvailable)
      questionEntries.push({ label: "Paperwork available", value: questions.isPaperworkAvailable });
  }

  // Activity timeline: merge moves + closures + payments
  const activityItems = [
    ...payments.map((p: { id: string; type: string; state: string; amount: number | null; createdAt: string }) => ({
      id: `p-${p.id}`,
      type: "payment" as const,
      title: p.amount != null ? `Payment ${p.state} · $${p.amount}` : `Payment ${p.state}`,
      detail: p.type,
      createdAt: p.createdAt,
      dotColor: p.state === "completed" ? "bg-sage" : p.state === "failed" ? "bg-rust" : "bg-spark",
    })),
    ...moves.map((m: { id: string; state: string; reason: string | null; createdAt: string }) => ({
      id: `m-${m.id}`,
      type: "move" as const,
      title: `Move ${m.state}`,
      detail: m.reason ?? "No reason given",
      createdAt: m.createdAt,
      dotColor: "bg-spark",
    })),
    ...closures.map((c: { id: string; totalMinutes: number; totalAmount: number; createdAt: string }) => ({
      id: `c-${c.id}`,
      type: "closure" as const,
      title: `Session closed · ${c.totalMinutes} min · $${c.totalAmount}`,
      detail: "Closure",
      createdAt: c.createdAt,
      dotColor: "bg-ink-5",
    })),
  ].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

  // Countdown to session start
  let startsInLabel: string | null = null;
  if (avail) {
    const dt = parseDateTime(avail.date, avail.startTime);
    if (dt) {
      const diffMs = dt.getTime() - Date.now();
      if (diffMs > 0 && diffMs < 24 * 60 * 60 * 1000) {
        const hrs = Math.floor(diffMs / (1000 * 60 * 60));
        const mins = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
        startsInLabel = `Starts in ${hrs}h ${mins}m`;
      }
    }
  }

  return (
    <div>
      {/* The page's own h1 is the session hero below, so the header row carries
          only the back link and the countdown. */}
      <PageHeader
        heading={false}
        back={{ to: "/sessions", label: "sessions" }}
        controls={
          startsInLabel && !isDeleted ? (
            <span className="flex items-center gap-1.5 text-[11.5px] text-ink-4">
              <span className="dot bg-tide pulse-soft" />
              {startsInLabel}
            </span>
          ) : undefined
        }
      />

      <div className="px-4 md:px-7 pb-6">

        {/* Deleted banner */}
        {isDeleted && (
          <div className="mb-6 px-5 py-4 rounded-lg bg-canvas-2 border border-ink/10 flex items-center justify-between gap-4 flex-wrap">
            <div>
              <div className="text-[13.5px] font-medium text-ink-2">This session is deleted</div>
              <div className="text-[12px] text-ink-4">Restore to make it visible to your team again.</div>
            </div>
            <button
              onClick={handleRestore}
              disabled={restoring}
              className="px-4 py-2 rounded-md bg-tide text-paper-hi text-[13px] font-medium hover:bg-tide-d disabled:opacity-50"
            >
              {restoring ? "Restoring…" : "Restore session"}
            </button>
          </div>
        )}

        {/* Hero */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
          <div className="lg:col-span-8">
            <div className="flex items-center gap-2 mb-3 flex-wrap">
              <BookingStateBadge state={booking.state} />
            </div>
            <h1 className="font-display text-[40px] font-medium leading-[1.05] tracking-tight">
              {booking.bookingType?.name ?? "Session"} · {primaryCustomer ? clientDisplayName(primaryCustomer) : (booking.primaryCustomerName ?? "Client")}
            </h1>
            <div className="mt-6 max-w-md">
              <BookingProgressBar
                state={booking.state}
                availabilityDate={avail?.date}
                availabilityEndTime={avail?.endTime}
              />
            </div>
          </div>

          {avail && (
            <div className="lg:col-span-4">
              <div className="bg-paper-hi rounded-lg shadow-card p-5">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <div className="text-ink-4 text-[10.5px] uppercase tracking-wider font-medium">
                      {formatRelative(avail.date, avail.startTime) === "Today" ? "Today" : formatRelative(avail.date, avail.startTime)}
                    </div>
                    <div className="font-display text-[28px] font-medium leading-none mt-1 num">
                      {formatTimeRange(avail.date, avail.startTime, avail.endTime)}
                    </div>
                    <div className="text-[12.5px] text-ink-3 mt-1">
                      {formatDateLabel(avail.date)} · {formatDuration(avail.startTime, avail.endTime, avail.date)}
                    </div>
                  </div>
                </div>
                <div className="bg-paper rounded p-3">
                  <div className="text-[10.5px] text-ink-4 uppercase tracking-wider font-medium">
                    Location
                  </div>
                  <div className="text-[13px] font-medium mt-1 flex items-center gap-1.5">
                    {booking.method === "in-person" && <MapPin size={12} strokeWidth={2} />}
                    {booking.method === "video" && <Video size={12} strokeWidth={2} />}
                    {booking.method === "phone" && <Phone size={12} strokeWidth={2} />}
                    {methodToLocation(booking.method)}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="px-7 hair-b">
        <div className="flex items-center gap-6 overflow-x-auto">
          {(
            [
              { key: "overview", label: "Overview" },
              { key: "files", label: "Files", count: files.length },
              { key: "transcript", label: "Transcript" },
              { key: "activity", label: "Activity", count: activityItems.length },
            ] as const
          ).map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`pb-3 text-[13px] flex items-center gap-2 border-b-2 whitespace-nowrap ${
                activeTab === tab.key
                  ? "font-medium text-ink border-tide"
                  : "text-ink-4 hover:text-ink-2 border-transparent"
              }`}
            >
              {tab.label}
              {"count" in tab && tab.count > 0 && (
                <span className="text-[11px] text-ink-4 num">{tab.count}</span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="px-7 py-8 pb-20">
        {activeTab === "files" ? (
          <FilesPanel
            files={files}
            selectedFileId={selectedFileId}
            onSelect={setSelectedFileId}
          />
        ) : (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-3">
        {/* Main column */}
        <div className="lg:col-span-8 space-y-3">
          {activeTab === "overview" && (
            <>
              {/* Session details — always renders */}
              <Card header={{ title: "Session details" }}>
                <div className="p-5">
                  <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">
                    {booking.bookingType?.name && (
                      <DetailRow label="Matter" value={booking.bookingType.name} />
                    )}
                    <DetailRow label="Method" value={methodToLocation(booking.method)} />
                    {booking.legalName && (
                      <DetailRow label="Legal name" value={booking.legalName} />
                    )}
                    {booking.customerCount > 1 && (
                      <DetailRow
                        label="Parties"
                        value={`${booking.customerCount} customers`}
                      />
                    )}
                    {booking.bookingSituation && (
                      <DetailRow
                        label="Situation"
                        value={humanize(booking.bookingSituation)}
                      />
                    )}
                    {booking.agreementType && (
                      <DetailRow
                        label="Agreement type"
                        value={humanize(booking.agreementType)}
                      />
                    )}
                    {booking.createdAt && (
                      <DetailRow
                        label="Booked"
                        value={`${timeAgo(booking.createdAt)}`}
                      />
                    )}
                  </dl>
                </div>
              </Card>

              {/* Client brief */}
              {questionEntries.length > 0 && (
                <Card
                  header={{
                    title: "Client brief",
                    action: <span className="text-[11px] text-ink-4">From intake form</span>,
                  }}
                >
                  <div className="p-5">
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                      {questionEntries.map((q) => (
                        <div key={q.label}>
                          <div className="text-[11px] text-ink-4 uppercase tracking-wider font-medium mb-1">
                            {q.label}
                          </div>
                          <div className="text-[13px] font-medium">{q.value}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                </Card>
              )}

              {/* Attached documents */}
              {files.length > 0 && (
                <Card header={{ title: "Attached documents" }}>
                  <div className="divide-hair">
                    {files.map(
                      (file: {
                        id: string;
                        originalFilename: string;
                        fileSize: number;
                        mimeType: string;
                        createdAt: string;
                      }) => (
                        <div
                          key={file.id}
                          className="px-5 py-3 flex items-center gap-3 hover:bg-paper-2 transition cursor-pointer"
                        >
                          <div
                            className={`w-9 h-10 rounded flex items-center justify-center flex-shrink-0 ${
                              file.mimeType.includes("pdf")
                                ? "bg-rust-s text-rust"
                                : "bg-sage-s text-sage"
                            }`}
                          >
                            <FileText size={14} strokeWidth={2} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-[13px] font-medium truncate">
                              {file.originalFilename}
                            </div>
                            <div className="text-[11.5px] text-ink-4">
                              {formatFileSize(file.fileSize)} · {file.mimeType}
                            </div>
                          </div>
                          <span className="text-[11px] text-ink-4 font-mono">
                            {timeAgo(file.createdAt)}
                          </span>
                        </div>
                      ),
                    )}
                  </div>
                </Card>
              )}
            </>
          )}

          {activeTab === "transcript" && (
            <TranscriptPanel
              loading={transcriptLoading}
              transcript={transcriptData?.sessionTranscript}
            />
          )}

          {activeTab === "activity" && (
            <Card header={{ title: "Activity" }}>
              {activityItems.length > 0 ? (
                <div className="divide-hair">
                  {activityItems.map((item) => (
                    <div key={item.id} className="px-5 py-3 flex items-start gap-3">
                      <div className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${item.dotColor}`} />
                      <div className="flex-1 min-w-0">
                        <div className="text-[13px] font-medium">{item.title}</div>
                        <div className="text-[11.5px] text-ink-4">{item.detail}</div>
                      </div>
                      <span className="text-[11px] text-ink-4 font-mono whitespace-nowrap">
                        {timeAgo(item.createdAt)}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="py-12 text-center text-[13px] text-ink-4">
                  No activity yet.
                </div>
              )}
            </Card>
          )}
        </div>

        {/* Sidebar */}
        <aside className="lg:col-span-4 space-y-3">
          {/* Actions — hidden when deleted */}
          {!isDeleted && (
            <div className="bg-paper-hi rounded-lg shadow-card">
              <div className="p-4 space-y-2">
                <Link
                  to={`/sessions/${id}/conduct`}
                  className="w-full px-4 py-3 rounded-md bg-tide text-paper-hi text-[14px] font-medium flex items-center justify-center gap-2 hover:bg-tide-d"
                >
                  <Play size={14} strokeWidth={2.2} />
                  Conduct session
                </Link>
                {booking.method !== "in-person" && (
                  <button
                    type="button"
                    onClick={handleSendLink}
                    disabled={sendingLink || linkJustSent}
                    className="w-full px-4 py-2.5 rounded-md hair border bg-paper text-[13px] text-ink-2 hover:bg-paper-2 flex items-center justify-center gap-2 font-medium disabled:opacity-50"
                  >
                    {linkJustSent ? (
                      <Check size={13} strokeWidth={2} />
                    ) : (
                      <Send size={13} strokeWidth={2} />
                    )}
                    {sendingLink
                      ? "Sending…"
                      : linkJustSent
                        ? "They'll get it shortly"
                        : "Send join link to client"}
                  </button>
                )}
                <div className="grid grid-cols-2 gap-2">
                  {canMove ? (
                    <Link
                      to={`/sessions/${id}/move`}
                      className="px-3 py-2 rounded-md hair border bg-paper text-[12.5px] text-ink-2 hover:bg-paper-2 flex items-center justify-center gap-1.5 font-medium"
                    >
                      <ArrowRightLeft size={12} strokeWidth={2} />
                      Move
                    </Link>
                  ) : (
                    <Tooltip label="Not available in this state">
                      <button
                        disabled
                        className="w-full px-3 py-2 rounded-md hair border bg-paper text-[12.5px] text-ink-4 flex items-center justify-center gap-1.5 font-medium opacity-50 cursor-not-allowed"
                      >
                        <ArrowRightLeft size={12} strokeWidth={2} />
                        Move
                      </button>
                    </Tooltip>
                  )}
                  {canCancel ? (
                    <Link
                      to={`/sessions/${id}/cancel`}
                      className="px-3 py-2 rounded-md hair border bg-paper text-[12.5px] text-rust hover:bg-rust-s/50 flex items-center justify-center gap-1.5 font-medium"
                    >
                      <X size={12} strokeWidth={2} />
                      Cancel
                    </Link>
                  ) : (
                    <Tooltip label="Not available in this state">
                      <button
                        disabled
                        className="w-full px-3 py-2 rounded-md hair border bg-paper text-[12.5px] text-ink-4 flex items-center justify-center gap-1.5 font-medium opacity-50 cursor-not-allowed"
                      >
                        <X size={12} strokeWidth={2} />
                        Cancel
                      </button>
                    </Tooltip>
                  )}
                </div>
                {canClose ? (
                  <Link
                    to={`/sessions/${id}/close`}
                    className="w-full px-3 py-2 rounded-md hair border bg-paper text-[12.5px] text-ink-2 hover:bg-paper-2 flex items-center justify-center gap-1.5 font-medium"
                  >
                    <Check size={12} strokeWidth={2} />
                    Close session
                  </Link>
                ) : (
                  <Tooltip label="Not available in this state">
                    <button
                      disabled
                      className="w-full px-3 py-2 rounded-md hair border bg-paper text-[12.5px] text-ink-4 flex items-center justify-center gap-1.5 font-medium opacity-50 cursor-not-allowed"
                    >
                      <Check size={12} strokeWidth={2} />
                      Close session
                    </button>
                  </Tooltip>
                )}
                <div className="hair-t mt-2 pt-2">
                  <Link
                    to={`/sessions/${id}/delete`}
                    className="block text-center text-[12px] text-ink-4 hover:text-rust py-1.5 transition"
                  >
                    Delete session
                  </Link>
                </div>
              </div>
            </div>
          )}

          {/* Client */}
          {primaryCustomer && (
            <div className="bg-paper-hi rounded-lg shadow-card">
              <div className="px-4 py-3 hair-b flex items-center justify-between">
                <h3 className="text-[13px] font-semibold">Client</h3>
                {/* Prefer the booking's own client_id (stamped at creation —
                    Client-stable) so historical sessions keep linking to the
                    Client they were booked under even after a contact moves. */}
                {(booking?.clientId || primaryCustomer.primaryClientId) && (
                  <Link
                    to={`/clients/${booking?.clientId ?? primaryCustomer.primaryClientId}`}
                    className="text-[11px] text-ink-4 hover:text-tide transition-colors"
                  >
                    View profile →
                  </Link>
                )}
              </div>
              <div className="p-4">
                <div className="flex items-center gap-3 mb-3">
                  <Avatar
                    initials={clientInitials(primaryCustomer)}
                    size="lg"
                    className="bg-tide text-paper-hi"
                  />
                  <div className="min-w-0 flex-1">
                    <div className="text-[13.5px] font-medium">
                      <ClientName customer={primaryCustomer} showSecondary secondaryClassName="text-[11.5px] text-ink-4 font-normal" />
                    </div>
                  </div>
                </div>
                <div className="space-y-2 text-[12.5px]">
                  {primaryCustomer.email && (
                    <div className="flex items-center gap-2 text-ink-3 min-w-0">
                      <Mail size={12} strokeWidth={1.8} className="text-ink-4 flex-shrink-0" />
                      <span className="truncate">{primaryCustomer.email}</span>
                    </div>
                  )}
                  {primaryCustomer.phoneNumber && (
                    <div className="flex items-center gap-2 text-ink-3 min-w-0">
                      <Phone size={12} strokeWidth={1.8} className="text-ink-4 flex-shrink-0" />
                      <span className="num">{primaryCustomer.phoneNumber}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Provider */}
          {providerName && (
            <div className="bg-paper-hi rounded-lg shadow-card">
              <div className="px-4 py-3 hair-b flex items-center justify-between">
                <h3 className="text-[13px] font-semibold">Provider</h3>
              </div>
              <div className="p-4">
                <div className="flex items-center gap-3">
                  <Avatar initials={providerInitials} size="lg" />
                  <div className="min-w-0 flex-1">
                    <div className="text-[13.5px] font-medium">{providerName}</div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Fee agreement */}
          {booking.agreementType && (
            <div className="bg-paper-hi rounded-lg shadow-card">
              <div className="px-4 py-3 hair-b flex items-center justify-between">
                <h3 className="text-[13px] font-semibold">Fee agreement</h3>
                <span className="text-[11px] text-ink-4">
                  {humanize(booking.agreementType)}
                </span>
              </div>
              <div className="p-4">
                {feeAgreementAvailable ? (
                  <button
                    type="button"
                    onClick={() => setAgreementOpen(true)}
                    className="w-full px-3 py-2 rounded-md hair border bg-paper text-[12.5px] text-ink-2 hover:bg-paper-2 flex items-center justify-center gap-1.5 font-medium"
                  >
                    <FileText size={12} strokeWidth={2} />
                    View fee agreement
                  </button>
                ) : (
                  <div className="text-[12px] text-ink-4">
                    Not generated yet — available once the client signs.
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Related */}
          {(payments.length > 0 || moves.length > 0 || closures.length > 0) && (
            <div className="bg-paper-hi rounded-lg shadow-card">
              <div className="px-4 py-3 hair-b flex items-center justify-between">
                <h3 className="text-[13px] font-semibold">Related</h3>
              </div>
              <div className="divide-hair">
                {payments.length > 0 && (
                  <div className="px-4 py-3 flex items-center gap-3">
                    <div className="w-7 h-7 rounded bg-sage-s flex items-center justify-center text-sage">
                      <CreditCard size={13} strokeWidth={2} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[12.5px] font-medium">
                        Payment {payments[0].state}
                      </div>
                      <div className="text-[11px] text-ink-4">
                        {payments[0].amount != null ? `$${payments[0].amount} · ` : ""}{payments[0].type}
                      </div>
                    </div>
                  </div>
                )}
                {moves.length > 0 && (
                  <div className="px-4 py-3 flex items-center gap-3">
                    <div className="w-7 h-7 rounded bg-spark-s flex items-center justify-center text-spark">
                      <ArrowRightLeft size={13} strokeWidth={2} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[12.5px] font-medium">
                        Move {moves[0].state}
                      </div>
                      <div className="text-[11px] text-ink-4">
                        {moves[0].reason ?? "No reason"} · {timeAgo(moves[0].createdAt)}
                      </div>
                    </div>
                  </div>
                )}
                {closures.length > 0 && (
                  <div className="px-4 py-3 flex items-center gap-3">
                    <div className="w-7 h-7 rounded bg-canvas-2 flex items-center justify-center text-ink-3">
                      <Clock size={13} strokeWidth={2} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[12.5px] font-medium">
                        Closed · {closures[0].totalMinutes} min
                      </div>
                      <div className="text-[11px] text-ink-4">
                        ${closures[0].totalAmount} · {timeAgo(closures[0].createdAt)}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </aside>
        </div>
        )}
      </div>

      {agreementOpen && booking.id && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-6 bg-ink/40 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
          aria-label="Fee agreement"
          onClick={(e) => {
            if (e.target === e.currentTarget) setAgreementOpen(false);
          }}
        >
          <div className="bg-paper-hi rounded-lg shadow-card w-full max-w-4xl h-[90vh] flex flex-col overflow-hidden">
            <div className="px-5 py-3 hair-b flex items-center justify-between">
              <div>
                <div className="text-[13.5px] font-semibold text-ink">
                  Fee agreement
                </div>
                <div className="text-[11.5px] text-ink-4">
                  {booking.bookingType?.name ?? "Session"} ·{" "}
                  {primaryCustomer ? clientDisplayName(primaryCustomer) : (booking.primaryCustomerName ?? "Client")}
                </div>
              </div>
              <button
                type="button"
                onClick={() => setAgreementOpen(false)}
                className="w-8 h-8 rounded-md flex items-center justify-center text-ink-4 hover:bg-paper-2 hover:text-ink-2"
                aria-label="Close"
              >
                <X size={14} strokeWidth={2} />
              </button>
            </div>
            <div className="flex-1 min-h-0 p-4">
              <FeeAgreementViewer
                bookingId={booking.id}
                height="100%"
                className="h-full"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
